// Libs
import classNames from 'classnames/bind';
import zIndex from '@mui/material/styles/zIndex';
import { useDraggable } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseStatus, UnderDevelopment } from '~/components/common';
import { LabelValueSchedule, SplitFormModal } from '~/components/module/order';
// Others
import { IOrderEventList } from '~/utils/interface/order';
import { formatDate, formatID, getFullName, navigateToOrderDetailByRole } from '~/utils/helper/common';
import { ColorTypeEnum, DateFormatEnum, ScheduleSplitType } from '~/utils/enum/common';
import { StorageEnum } from '~/utils/enum/base';
// Styles, images, icons
import styles from './ItemOrderSchedule.module.scss';

type Props = {
  item: IOrderEventList;
  onStartDrag?: (item: IOrderEventList) => void;
};

const cx = classNames.bind(styles);

const ItemOrderSchedule = (props: Props) => {
  //#region Destructuring Props
  const { item, onStartDrag } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/order', 'common/base']);
  const navigate = useNavigate();
  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: item?.id,
    data: item,
  });
  const [isOpenSplitFormModal, setIsOpenSplitFormModal] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (anchorEl && !!transform) {
      setAnchorEl(null);
    }
  }, [transform]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleShowSplitFormModal = () => {
    setIsOpenSplitFormModal(!isOpenSplitFormModal);
    setAnchorEl(null);
  };

  const handleNavigateDetailOrder = () => {
    navigateToOrderDetailByRole({
      navigate,
      role: currentRole,
      orderId: item?.orderId,
    });
  };
  //#endregion Handle Function

  return (
    <Box
      style={{
        transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
        cursor: transform ? 'grabbing' : 'grab',
        zIndex: transform ? zIndex.modal + 1 : undefined,
      }}
    >
      <Box
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        id='itemOrderScheduleComponent'
        className={cx('container', item?.color === ColorTypeEnum.RED ? 'red' : 'not-color')}
        onMouseDown={() => onStartDrag?.(item)}
      >
        <Stack direction={'row'} spacing={2} justifyContent={'space-between'} alignItems={'center'}>
          <Stack onClick={handleNavigateDetailOrder} className={cx('orderNumber')}>
            <Typography className={cx('valueOrderId')}>{`# ${formatID(item?.orderId)}`}</Typography>
          </Stack>

          {item?.isDelivery && <BaseStatus label={t('order_item_delivery')} />}

          {item?.isPickup && <BaseStatus label={t('order_item_pickup')} />}
        </Stack>

        <Typography className={cx('orderName')}>{item?.eventTitle}</Typography>

        <Stack spacing={1}>
          <LabelValueSchedule
            typeStyle='medium'
            label={t('order_item_label_assigned')}
            value={getFullName({ firstName: item?.assignedUser?.firstName, lastName: item?.assignedUser?.lastName })}
          />

          <LabelValueSchedule
            typeStyle='medium'
            label={t('order_item_label_date')}
            value={formatDate(item?.startDate, DateFormatEnum.MM_DD_YYYY)}
          />

          <LabelValueSchedule
            typeStyle='medium'
            label={t('order_item_label_location')}
            value={item?.address?.address}
          />

          <LabelValueSchedule
            typeStyle='medium'
            label={t('order_item_label_tag')}
            value={item?.tag?.charAt(0).toUpperCase() + item?.tag?.slice(1)}
          />
        </Stack>

        <Button
          id='btnMenuItemOrderSchedule'
          className={cx('buttonMenu')}
          variant='contained'
          onClick={handleShowSplitFormModal}
        >
          {t('order_item_button_split')}
        </Button>

        {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
      </Box>

      {isOpenSplitFormModal && (
        <SplitFormModal item={item} onClose={handleShowSplitFormModal} type={ScheduleSplitType.AVAILABLE_ORDERS} />
      )}
    </Box>
  );
};

export default ItemOrderSchedule;
